import { useState, useEffect, useMemo, useRef } from 'react';
import { Tree, TreeSelectionEvent, TreeCheckboxSelectionKeys } from 'primereact/tree';
import { TreeNode } from 'primereact/treenode';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import menuService from './menu-service';
import { Fieldset } from 'primereact/fieldset';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';

interface DropdownItem {
    referenceName: string;
    referenceCode: string;
    fullDesc: string;
    objectId: string;
    moduleId: string;
}

const MenuManagement: React.FC = () => {
    const [treeNodes, setTreeNodes] = useState<TreeNode[] | null>([]);
    const [selectedKeysArray, setSelectedKeysArray] = useState<string[]>([]);
    const [formData, setFormData] = useState<any>({});
    const [menuSetDropdown, setMenuSetDropdown] = useState<DropdownItem[]>([]);
    const [moduleDropdown, setModuleDropdown] = useState<DropdownItem[]>([]);
    const [clientState, setClientState] = useState<any>(null);
    const [selectedNode, setSelectedNode] = useState<any>(null);
    const toast = useRef<Toast>(null);
    const navigate = useNavigate();

    const menuSetDropdownOptions = useMemo(() => menuSetDropdown, [menuSetDropdown]);
    const moduleDropdownOptions = useMemo(() => moduleDropdown, [moduleDropdown]);

    const defaultNode = {
        objectId: null,
        moduleId: null,
        fullDesc: null,
        shortDesc: null,
        imageUrl: null,
        parameter: null,
        recordStatus: true,
        parentObjectId: null,
        orderSeq: null,
        menuSetObjectId: null,
    }

    useEffect(() => {
        const fetchClientState = async () => {
            try {
                menuService.getClientState().then((data) => {
                    setClientState(data);
                });

                menuService.getMenuSetDropdownByObjectType('M').then((data) => {
                    setMenuSetDropdown(data);
                });

                menuService.getModuleDropdown().then((data) => {
                    setModuleDropdown(data);
                });

            } catch (error) {
                console.error('Error fetching client state:', error);
            }
        };

        fetchClientState();
    }, []);


    const handleSelectionChange = (event: TreeSelectionEvent) => {

        setSelectedNode(null);

        menuService.getMenuById(event.value).then((data) => {
            data.recordStatus = data.recordStatus == '1' ? true : false;
            setSelectedNode(data);
        });


    };

    const handleInputChange = (e: any) => {
        const { id, value } = e.target;
        setSelectedNode({
            ...selectedNode,
            [id]: value
        });
    };

    const handleMenusetDropdownChange = (e: any) => {
        const { id, value } = e.target;
        menuService.getADPMenuTree(value.objectId).then((data) => {
            const menuTree: TreeNode[] = data;
            setTreeNodes(menuTree);
            setFormData({
                ...formData,
                [id]: value
            });

        });
    };

    const handleDropdownChange = (e: any) => {
        const { id, value } = e.target;
        setSelectedNode({
            ...selectedNode,
            [id]: value
        });
    };



    const treeTemplate = () => {
        function addChild(): void {

            const newChildNode = defaultNode;

            newChildNode.moduleId = selectedNode.moduleId;
            newChildNode.parentObjectId = selectedNode.objectId;
            newChildNode.menuSetObjectId = selectedNode.menuSetObjectId;

            setSelectedNode(newChildNode);
        }

        return (
            <>
                <Button label="Add Child Node" icon="pi pi-check" style={{ marginRight: '.5em' }} onClick={addChild} />

            </>
        );
    };

    const toolbarTemplate = () => {
        function submit(): void {
            const currentDate = new Date().toLocaleString("en-US", { timeZone: clientState.timeZone });

            selectedNode.modifiedDate = new Date(currentDate);
            selectedNode.modifiedBy = clientState.fullName;
            selectedNode.recordStatus = selectedNode.recordStatus == true ? '1' : '0';

            if (selectedNode.objectId === null) {
                selectedNode.insertDate = new Date(currentDate);
                selectedNode.insertBy = clientState.fullName;
                selectedNode.facilityCode = clientState.facilityCode;
                selectedNode.objectType = 'O';
                selectedNode.fullDescEnglish = selectedNode.fullDesc;
                selectedNode.shortDescEnglish = selectedNode.shortDesc;
                selectedNode.functionList = 'VIEW,EDIT,DELETE';

                menuService.create(selectedNode).then(() => {
                    selectedNode.recordStatus = selectedNode.recordStatus == '1' ? true : false;
                    menuService.getADPMenuTree(selectedNode.menuSetObjectId).then((data) => {
                        const menuTree: TreeNode[] = data;
                        setTreeNodes(menuTree);
                    });

                    clientState.success.detail = 'Already Save Record';
                    toast.current?.show(clientState.success);
                    navigate("/admin/menu");
                }).catch((error) => {
                    const errorMessage = error.response?.data?.message || 'Failed to create record.';
                    clientState.error.detail = errorMessage;
                    toast.current?.show(clientState.error);
                });
            } else {
                menuService.update(selectedNode).then(() => {
                    selectedNode.recordStatus = selectedNode.recordStatus == '1' ? true : false;
                    menuService.getADPMenuTree(selectedNode.menuSetObjectId).then((data) => {
                        const menuTree: TreeNode[] = data;
                        setTreeNodes(menuTree)

                    });
                    clientState.success.detail = 'Already Save Record';
                    toast.current?.show(clientState.success);
                    navigate("/admin/menu");
                }).catch((error) => {
                    const errorMessage = error.response?.data?.message || 'Failed to create record.';
                    clientState.error.detail = errorMessage;
                    toast.current?.show(clientState.error);
                });
            }

        }

        function handleBack(): void {
            menuService.getMenuById(selectedNode.objectId).then((data) => {
                data.recordStatus = data.recordStatus == '1' ? true : false;
                setSelectedNode(data);
            });
        }

        function handleDelete(): void {

            menuService.deleteMenuTree(selectedNode.objectId).then(() => {
                menuService.getADPMenuTree(selectedNode.menuSetObjectId).then((data) => {
                    const menuTree: TreeNode[] = data;
                    setTreeNodes(menuTree)

                });
                setSelectedNode(null);
                clientState.success.detail = 'Already Delete Record';
                toast.current?.show(clientState.success);
                navigate("/admin/menu");
            }).catch((error) => {
                const errorMessage = error.response?.data?.message || 'Failed to Delete record.';
                clientState.error.detail = errorMessage;
                toast.current?.show(clientState.error);
            });;
        }

        return (
            <>
                <Toast ref={toast} />
                <Button label="Save" icon="pi pi-check" style={{ marginRight: '.5em' }} className="p-button-success" onClick={submit} />
                <Button label="Cancel" icon="pi pi-times" style={{ marginRight: '.5em' }} onClick={() => handleBack()} />
                <Button label="Delete" icon="pi pi-trash" style={{ marginRight: '.5em' }} onClick={() => handleDelete()} />
            </>
        );
    };

    return (
        <div className="grid">
            <div className="col-6">
                <div className="card">
                    <Fieldset legend="Menu Tree">
                        <div className="p-fluid form-grid grid">
                            <div className="field col-12 md:col-12">
                                <label htmlFor="menuSet">Menu set</label>
                                <Dropdown
                                    id="menuSet"
                                    value={formData.menuSet}
                                    onChange={handleMenusetDropdownChange}
                                    options={menuSetDropdownOptions}
                                    optionLabel="fullDesc"
                                    placeholder="Select One"
                                >
                                </Dropdown>
                            </div>
                        </div>
                        <Tree
                            value={treeNodes || []}
                            selectionMode="single"
                            selectionKeys={selectedNode}
                            onSelectionChange={handleSelectionChange}
                        />
                    </Fieldset>
                    <div>
                        <br />
                    </div>
                    <Toolbar end={treeTemplate}></Toolbar>
                </div>
            </div>
            <div className="col-6">
                <div className="card">
                    <Fieldset legend="Menu Information">
                        <div className="p-fluid form-grid grid">
                            <div className="field col-12 md:col-12">
                                <label htmlFor="objectId">Customer Code</label>
                                <InputText
                                    id="objectId"
                                    value={selectedNode?.objectId || ''}
                                    onChange={handleInputChange}
                                    disabled
                                />
                            </div>
                            <div className="field col-12 md:col-12">
                                <label htmlFor="moduleId">Menu set</label>
                                <Dropdown
                                    id="moduleId"
                                    value={selectedNode?.moduleId || ''}
                                    onChange={handleDropdownChange}
                                    options={moduleDropdownOptions}
                                    optionLabel="fullDesc"
                                    placeholder="Select One"
                                    optionValue="moduleId"
                                >
                                </Dropdown>
                            </div>
                            <div className="field col-12 md:col-12">
                                <label>Object Full Name</label>
                                <InputText
                                    id="fullDesc"
                                    value={selectedNode?.fullDesc || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-12">
                                <label>Object Short Name</label>
                                <InputText
                                    id="shortDesc"
                                    value={selectedNode?.shortDesc || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-12">
                                <label>Program Url</label>
                                <InputText
                                    id="programUrl"
                                    value={selectedNode?.programUrl || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-12">
                                <label>Image Url</label>
                                <InputText
                                    id="imageUrl"
                                    value={selectedNode?.imageUrl || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-12">
                                <label>Parameter</label>
                                <InputText
                                    id="parameter"
                                    value={selectedNode?.parameter || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <h6>Status</h6>
                                <InputSwitch
                                    checked={selectedNode?.recordStatus || ''}
                                    onChange={(e) => setSelectedNode({ ...selectedNode, recordStatus: e.value })}
                                />
                            </div>
                            <div className="field col-12 md:col-12">
                                <label>Parent Object Id</label>
                                <InputText
                                    id="parentObjectId"
                                    value={selectedNode?.parentObjectId || ''}
                                    onChange={handleInputChange}
                                    disabled
                                />
                            </div>
                            <div className="field col-12 md:col-12">
                                <label>Order Sequence</label>
                                <InputText
                                    id="orderSeq"
                                    value={selectedNode?.orderSeq || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </Fieldset>
                    <div>
                        <br />
                    </div>
                    <Toolbar end={toolbarTemplate}></Toolbar>
                </div>
            </div>
        </div>

    );
};

export default MenuManagement;
